/**
 * Anonymous visitor id, shared by every analytics surface on the site.
 *
 * A first-party cookie rather than localStorage so it survives across tabs and is
 * readable during a server render if we ever need it. It identifies a browser, never
 * a person — a signed-in visitor is additionally attributed server-side from the
 * bearer token, which is the only source the API trusts for `userId`.
 *
 * Lives here rather than under `lib/compare/` because compare analytics and CTA
 * analytics must agree on who a visitor is: two cookies would make the same person look
 * like two people across the two dashboards.
 */

const COOKIE_NAME = "pr_vid";
const ONE_YEAR_SECONDS = 60 * 60 * 24 * 365;

// Must satisfy the API's validator, which also hashes this into a Postgres advisory
// lock key: /^[A-Za-z0-9_-]{8,64}$/
const VALID_ID = /^[A-Za-z0-9_-]{8,64}$/;

function readCookie(name) {
  const match = document.cookie.match(
    new RegExp(`(?:^|;\\s*)${name}=([^;]*)`)
  );
  return match ? decodeURIComponent(match[1]) : null;
}

function createId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }

  if (typeof crypto !== "undefined" && typeof crypto.getRandomValues === "function") {
    const bytes = crypto.getRandomValues(new Uint8Array(16));
    return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
  }

  return `v${Date.now().toString(36)}${Math.random().toString(36).slice(2, 12)}`;
}

/** Existing visitor id, minting and persisting one on first visit. */
export function getVisitorId() {
  if (typeof document === "undefined") return null;

  try {
    const existing = readCookie(COOKIE_NAME);
    if (existing && VALID_ID.test(existing)) return existing;

    const id = createId();
    const secure = window.location.protocol === "https:" ? "; Secure" : "";
    document.cookie = `${COOKIE_NAME}=${id}; Max-Age=${ONE_YEAR_SECONDS}; Path=/; SameSite=Lax${secure}`;

    return id;
  } catch {
    // Cookies blocked — the visit simply goes untracked rather than breaking the page.
    return null;
  }
}

/** A fresh id per click, used by CTA tracking to make a re-sent flush a no-op. */
export function createEventId() {
  return createId();
}
